import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import cx from 'classnames';
import { Col } from 'react-bootstrap';
import FacilityImage from '../../../../containers/FacilityImage';
import SMFacilityLink from '../SMFacilityLink';
import Translation from '../../../../components/stateless/Translation';
import SMMapPinSecondary from '../SMMapPinSecondary';
import SMFacilitiesListItemBanner from '../SMFacilitiesListItemBanner';
import Reviews from './Reviews';
import Hours from './Hours';
import UnitListing from './UnitListing';
import styles from './styles.scss';
import ThemedButton from '../../../../components/stateless/ThemedButton';

const propTypes = {
  active: PropTypes.bool,
  facility: PropTypes.shape({
    id: PropTypes.string.isRequired,
    address: PropTypes.shape().isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onHover: PropTypes.func,
  multiLocale: PropTypes.bool,
  moveInPath: PropTypes.string,
  selectedUnitSize: PropTypes.string,
  selectedAmenities: PropTypes.arrayOf(PropTypes.string),
};

const defaultProps = {
  active: undefined,
  onHover: undefined,
  multiLocale: false,
  moveInPath: undefined,
  selectedUnitSize: undefined,
  selectedAmenities: [],
};

const SMFacilitiesListItemExpanded = ({
  active,
  facility,
  index,
  onHover,
  multiLocale,
  moveInPath,
  selectedUnitSize,
  selectedAmenities,
  theme,
}) => {
  const classes = cx(
    styles.item,
    active && styles.active,
    'row',
  );

  const onMouseLeave = onHover ? () => onHover() : undefined;
  const onMouseEnter = onHover ? () => onHover(facility) : undefined;

  const {
    address,
  } = facility;

  return (
    <li
      className={classes}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
    >
      <Col xs={5} lg={3}>
        <SMFacilitiesListItemBanner facility={facility} inline device="mobile" />
        <SMFacilityLink
          facility={facility}
          multiLocale={multiLocale}
          className={styles.facilityLink}
        >
          <FacilityImage
            className={styles.image}
            facility={facility}
          />
        </SMFacilityLink>
        <SMFacilitiesListItemBanner facility={facility} inline device="desktop" />
        <SMFacilityLink
          facility={facility}
          multiLocale={multiLocale}
          className={cx(styles.facilityLink, styles.mobileFacilityLink)}
          anchorTag="unitstable"
        >
          <ThemedButton
            context="bare"
            className={styles.reserveButton}
          >
            <Translation
              id="enterprise.widgets.nap.seeAvailableUnits"
              defaultMessage="See Available Units"
            />
          </ThemedButton>
        </SMFacilityLink>
      </Col>
      <Col xs={7} lg={9}>
        <div className={styles.content}>
          <SMMapPinSecondary
            className={styles.marker}
            index={index}
            sizeRatio={0.6}
          />
          <div className={styles.info}>
            <div className={styles.address}>
              <div className="clearfix">
                <SMFacilityLink
                  facility={facility}
                  multiLocale={multiLocale}
                  className={styles.facilityLink}
                >
                  <h5>{address.address1}</h5>
                  <h5 data-testid="sm-facilities-list-item-info-address2">
                    {address.address2}
                  </h5>
                  <h5>{`${address.city}, ${address.state} ${address.postal}`}</h5>
                </SMFacilityLink>
                <SMFacilityLink
                  facility={facility}
                  multiLocale={multiLocale}
                  className={cx(styles.facilityLink, styles.desktopFacilityLink)}
                  anchorTag="unitstable"
                >
                  <ThemedButton
                    context="bare"
                    className={styles.reserveButton}
                  >
                    <Translation
                      id="enterprise.widgets.nap.seeAvailableUnits"
                      defaultMessage="See Available Units"
                    />
                  </ThemedButton>
                </SMFacilityLink>
              </div>
              <Reviews facility={facility} multiLocale={multiLocale} />
              <Hours facility={facility} />
              <div className="hidden-xs hidden-sm visible-md-block visible-lg-block">
                <UnitListing
                  facility={facility}
                  moveInPath={moveInPath}
                  selectedUnitSize={selectedUnitSize}
                  selectedAmenities={selectedAmenities}
                  multiLocale={multiLocale}
                  theme={theme}
                />
              </div>
            </div>
          </div>
        </div>
      </Col>
      <Col xs={12} mdHidden lgHidden>
        <UnitListing
          facility={facility}
          moveInPath={moveInPath}
          selectedUnitSize={selectedUnitSize}
          selectedAmenities={selectedAmenities}
          multiLocale={multiLocale}
          theme={theme}
        />
      </Col>
    </li>
  );
};

SMFacilitiesListItemExpanded.propTypes = propTypes;
SMFacilitiesListItemExpanded.defaultProps = defaultProps;

SMFacilitiesListItemExpanded.fragments = {
  facility: gql`
    fragment SMFacilitiesListItemExpandedFacility on Facility {
      id
      address {
        address1
        address2
        city
        state
        postal
      }
      distanceFromCurrentUserInKilometers
      distanceFromCurrentUserInMiles

      ...FacilityImageFacility
      ...SMFacilityLinkFacility
      ...SMFacilitiesListItemBannerFacility
      ...HoursFacility
      ...ReviewsFacility
      ...UnitGroupsFacility
    }

    ${FacilityImage.fragments.facility}
    ${SMFacilityLink.fragments.facility}
    ${SMFacilitiesListItemBanner.fragments.facility}
    ${Hours.fragments.facility}
    ${Reviews.fragments.facility}
    ${UnitListing.fragments.facility}
  `,
};

export default SMFacilitiesListItemExpanded;
